import { FC, useState, useRef, useEffect } from "react";
import Image from "next/image";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useDraggable } from "react-use-draggable-scroll";
import { useAnimation, motion } from "framer-motion";
import { FullWidthLayout, SectionLayout } from "@/layouts";
import { HomeTitle } from "@/components/ui";
import { Selector } from "./selectors";
import {
  BenefitsTextContainer,
  MainScreenContainer,
  MockupContainer,
  MockupDeskBox,
  Selectors,
  MockupMobilBox,
  ImgWrapper,
} from "./benefits.styles";

import * as Styled from "../home.styles";

interface Props {
  selecteditem?: number;
}

const BenefitsScreen: FC<Props> = ({ selecteditem }) => {
  const { t } = useTranslation("global");

  const refScroll = useRef(null);
  const [ref, inView] = useInView();
  const controls = useAnimation();
  const { events } = useDraggable(refScroll as any);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const variants = {
    hidden: { opacity: 0, y: 50 },

    visible: ({ delay }: { delay: number }) => ({
      opacity: 1,
      transition: {
        delay,
        duration: 0.8,
      },
      y: 0,
    }),
  };

  const [selected, setSelected] = useState("");

  const handleSelector = (item: string) => {
    if (selected === item) {
      setSelected("");
    } else {
      setSelected(item);
    }
  };

  const mockupSelectorsSmall = [
    "increaseProfit",
    "extendLifetime",
    "easySetup",
    "beIndependent",
    "protectEnvironment",
    "OperateInterest",
  ];

  return (
    <FullWidthLayout id="benefits">
      <MainScreenContainer>
        <BenefitsTextContainer>
          <Styled.TitleContainer>
            <HomeTitle
              className="headline-small weight-medium"
              mb="1.875rem"
              color={
                selecteditem === 6 || selecteditem === 7
                  ? "var(--primary-dark)"
                  : "var(--primary-light)"
              }
              borderColor={
                selecteditem === 6 || selecteditem === 7
                  ? "var(--primary-dark)"
                  : "var(--primary-light)"
              }
            >
              {t("benefits.title")}
            </HomeTitle>

            <Styled.SectionTitle
              className="display-large weight-medium"
              color={
                selecteditem === 6 || selecteditem === 7
                  ? "var(--on-surface-dark)"
                  : "var(--on-surface-light)"
              }
            >
              {t("benefits.description")}
            </Styled.SectionTitle>
          </Styled.TitleContainer>
        </BenefitsTextContainer>
        <MockupContainer>
          <div>
            <MockupMobilBox>
              <ImgWrapper>
                <Image
                  src="/assets/benefits/mockups/mockupMobile.webp"
                  alt="cellect-energy interface"
                  fill
                  style={{ objectFit: "cover" }}
                  quality={100}
                />
              </ImgWrapper>
            </MockupMobilBox>
            <MockupDeskBox>
              <ImgWrapper>
                <Image
                  src="/assets/benefits/mockups/mockupDesk.webp"
                  alt="cellect-energy interface"
                  fill
                  style={{ objectFit: "cover" }}
                  quality={100}
                />
              </ImgWrapper>
            </MockupDeskBox>
          </div>

          <div ref={ref}>
            <Selectors {...events} ref={refScroll}>
              {mockupSelectorsSmall.map((item, index) => (
                <motion.div
                  key={index}
                  variants={variants}
                  initial="hidden"
                  animate={controls}
                  custom={{ delay: index * 0.3 }}
                >
                  <Selector
                    selecteditem={selecteditem}
                    key={index}
                    benefit={item}
                    idx={index}
                    selected={selected}
                    handleSelector={handleSelector}
                  />
                </motion.div>
              ))}
            </Selectors>
          </div>
        </MockupContainer>
      </MainScreenContainer>
    </FullWidthLayout>
  );
};

export default BenefitsScreen;
